/**
 * Lazy-loaded framer-motion components to reduce initial bundle size.
 *
 * framer-motion's projection system adds ~80KB to the bundle even if you only
 * use simple animations. By lazy-loading, we defer this cost until the user
 * interacts with the page (click or scroll).
 *
 * Usage:
 *   import { useInteractionMotion } from 'lib/lazy-framer-motion';
 *
 *   const { motion, useScroll, useTransform, isLoaded } = useInteractionMotion();
 *
 *   // Before interaction, motion components are null - render static fallbacks
 *   // After interaction, framer-motion loads and components become available
 */
import React, { lazy, Suspense, useEffect, useState, useMemo } from 'react'

import type { HTMLMotionProps, MotionValue, SVGMotionProps } from 'framer-motion'

// Global state for interaction tracking
let hasUserInteracted = false
let interactionListeners: Array<() => void> = []
let framerMotionModule: typeof import('framer-motion') | null = null
let loadingPromise: Promise<typeof import('framer-motion')> | null = null

const triggerInteraction = () => {
  if (hasUserInteracted) return
  hasUserInteracted = true
  interactionListeners.forEach((listener) => listener())
  interactionListeners = []
}

const setupInteractionListeners = () => {
  if (typeof window === 'undefined') return

  const handleInteraction = () => {
    triggerInteraction()
    // Clean up listeners after first interaction
    window.removeEventListener('wheel', handleInteraction)
    window.removeEventListener('click', handleInteraction)
    window.removeEventListener('touchstart', handleInteraction)
    window.removeEventListener('keydown', handleInteraction)
  }

  // Trigger on real user *input* (wheel/touch/click/key), NOT the `scroll` event:
  // Next's manual scroll-restoration-to-top and layout shifts fire `scroll`
  // during page load, which tripped this loader and pulled the ~74KB framer
  // chunk onto the FCP critical path (it's only needed for the below-the-fold
  // wizard and click-opened modals). wheel/touchstart fire only on genuine
  // scroll input, so framer stays out of the initial load.
  window.addEventListener('wheel', handleInteraction, { once: true, passive: true })
  window.addEventListener('click', handleInteraction, { once: true })
  window.addEventListener('touchstart', handleInteraction, { once: true, passive: true })
  window.addEventListener('keydown', handleInteraction, { once: true })
}

// Initialize listeners on module load (client-side only)
if (typeof window !== 'undefined') {
  setupInteractionListeners()
}

const loadFramerMotion = (): Promise<typeof import('framer-motion')> => {
  if (framerMotionModule) return Promise.resolve(framerMotionModule)
  if (loadingPromise) return loadingPromise

  loadingPromise = import('framer-motion').then((mod) => {
    framerMotionModule = mod
    return mod
  })

  return loadingPromise
}

// Types for the hook return
type FramerMotionModule = typeof import('framer-motion')

export interface InteractionMotionResult {
  motion: FramerMotionModule['motion'] | null
  AnimatePresence: FramerMotionModule['AnimatePresence'] | null
  useScroll: FramerMotionModule['useScroll'] | null
  useTransform: FramerMotionModule['useTransform'] | null
  useMotionValueEvent: FramerMotionModule['useMotionValueEvent'] | null
  useAnimation: FramerMotionModule['useAnimation'] | null
  isLoaded: boolean
  MotionValue: typeof MotionValue | null
}

/**
 * Hook that loads framer-motion only after user interaction (click or scroll).
 * Returns null values until loaded, allowing components to render static fallbacks.
 */
export const useInteractionMotion = (): InteractionMotionResult => {
  const [module, setModule] = useState<FramerMotionModule | null>(framerMotionModule)
  const [isLoaded, setIsLoaded] = useState(!!framerMotionModule)

  useEffect(() => {
    // If already loaded, we're done
    if (framerMotionModule) {
      setModule(framerMotionModule)
      setIsLoaded(true)
      return
    }

    // If user already interacted, load immediately
    if (hasUserInteracted) {
      loadFramerMotion().then((mod) => {
        setModule(mod)
        setIsLoaded(true)
      })
      return
    }

    // Otherwise, wait for interaction
    const handleInteraction = () => {
      loadFramerMotion().then((mod) => {
        setModule(mod)
        setIsLoaded(true)
      })
    }

    interactionListeners.push(handleInteraction)

    return () => {
      const index = interactionListeners.indexOf(handleInteraction)
      if (index > -1) {
        interactionListeners.splice(index, 1)
      }
    }
  }, [])

  return useMemo(
    () => ({
      motion: module?.motion ?? null,
      AnimatePresence: module?.AnimatePresence ?? null,
      useScroll: module?.useScroll ?? null,
      useTransform: module?.useTransform ?? null,
      useMotionValueEvent: module?.useMotionValueEvent ?? null,
      useAnimation: module?.useAnimation ?? null,
      MotionValue: module ? (module.MotionValue as typeof MotionValue) : null,
      isLoaded,
    }),
    [module, isLoaded]
  )
}

// Lazy-loaded motion components (legacy - prefer useInteractionMotion)
const LazyMotionDivInner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.div,
  }))
)

const LazyMotionSpanInner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.span,
  }))
)

const LazyMotionH1Inner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.h1,
  }))
)

const LazyMotionButtonInner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.button,
  }))
)

const LazyMotionSvgInner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.svg,
  }))
)

const LazyMotionPathInner = lazy(() =>
  import('framer-motion').then((mod) => ({
    default: mod.motion.path,
  }))
)

// Wrapper components with Suspense. The fallbacks render plain DOM elements,
// which can't accept framer-motion's animation props (animate, initial, …) — so
// the fallback only forwards the DOM-safe props (className/style/children) and
// casts style to React.CSSProperties (framer-motion's MotionStyle is a superset).
export const LazyMotionDiv: React.FC<HTMLMotionProps<'div'>> = (props) => (
  <Suspense
    fallback={
      <div className={props.className} style={props.style as React.CSSProperties}>
        {props.children as React.ReactNode}
      </div>
    }
  >
    <LazyMotionDivInner {...props} />
  </Suspense>
)

export const LazyMotionSpan: React.FC<HTMLMotionProps<'span'>> = (props) => (
  <Suspense
    fallback={
      <span className={props.className} style={props.style as React.CSSProperties}>
        {props.children as React.ReactNode}
      </span>
    }
  >
    <LazyMotionSpanInner {...props} />
  </Suspense>
)

export const LazyMotionH1: React.FC<HTMLMotionProps<'h1'>> = (props) => (
  <Suspense
    fallback={
      <h1 className={props.className} style={props.style as React.CSSProperties}>
        {props.children as React.ReactNode}
      </h1>
    }
  >
    <LazyMotionH1Inner {...props} />
  </Suspense>
)

export const LazyMotionButton: React.FC<HTMLMotionProps<'button'>> = (props) => (
  <Suspense
    fallback={
      <button data-tracking="ignore" className={props.className} style={props.style as React.CSSProperties}>
        {props.children as React.ReactNode}
      </button>
    }
  >
    <LazyMotionButtonInner {...props} />
  </Suspense>
)

export const LazyMotionSvg: React.FC<SVGMotionProps<SVGSVGElement>> = (props) => (
  <Suspense
    fallback={<svg className={props.className as string | undefined} style={props.style as React.CSSProperties} />}
  >
    <LazyMotionSvgInner {...props} />
  </Suspense>
)

export const LazyMotionPath: React.FC<SVGMotionProps<SVGPathElement>> = (props) => (
  <Suspense
    fallback={<path className={props.className as string | undefined} style={props.style as React.CSSProperties} />}
  >
    <LazyMotionPathInner {...props} />
  </Suspense>
)
